import type { AudienceCreateDto } from '../../../modules/eventsApi/audiences/dto/audience-create.dto'
import type { AudienceRuleCreateDto } from '../../../modules/eventsApi/audienceRules/audience-rule-create.dto'
import { AudienceRuleAct, AudienceRuleDateReference, AudienceRuleDateType } from '../../../modules/eventsApi/audienceRules/audiences.enum'
import { AutomationSourceEnum } from '../../../modules/automationApi/automations/automation-source.enum'
import type { CreateAutomationGroupsDto } from '../../../modules/automationApi/automation-groups/dto/create-automation-groups.dto'
import type { CreateAutomationDto } from '../../../modules/automationApi/automations/dto/create-automation.dto'
import { DEFAULT_MESSAGES } from '../default-messages'

/**
 * Catálogo de templates de público.
 *
 * ## LEIA ANTES DE USAR OS NÚMEROS DE ADOÇÃO
 *
 * Os templates marcados como `TemplateOrigin.observed` saíram de um levantamento da base de
 * produção (5.814 públicos dinâmicos ativos em 576 empresas). MAS a maior parte desses públicos
 * foi criada pelo time de CS, não pelo cliente final — a razão regras/empresas denuncia isso:
 * `agendamento_criado[OR,20h..30h]` aparece 421 vezes em 416 empresas (1,01 por empresa), e
 * `agendamento_compareceu[OR,-168h..-24h]` 246 vezes em 246 empresas (1,00). Exatamente um por
 * empresa é assinatura de implantação em série, não de escolha independente.
 *
 * Consequências práticas:
 *
 * - `usage.empresas` mede ALCANCE DA IMPLANTAÇÃO, não preferência do usuário. **Não exibir como
 *   prova social** ("377 empresas usam este template") — seria enganoso.
 * - A ausência de um padrão na base não prova que ele não serve. Prova que o playbook do CS não
 *   o contemplava. `AudienceRuleAct.IncludeAnd` aparece em só 20 empresas, e ainda assim é o
 *   operador certo para segmentar por risco (ver `amanha-ja-faltou`).
 * - Nada aqui foi testado contra resultado de negócio. `totalDistinct` mede alcance; ninguém
 *   mede conversão.
 *
 * A tabela `audience` não tem `userId`, então não há como separar público criado pelo CS de
 * público criado pelo cliente — nem retroativamente, nem daqui para a frente. Uma coluna
 * `createdByUserId` (com `user.status >= 200` marcando equipe interna) é pré-requisito para
 * medir se este assistente funciona.
 *
 * Os templates `TemplateOrigin.proposed` não têm uso medido: nasceram de cruzar os eventos que
 * o produto emite com os que nenhum público consome.
 *
 * ## Defaults do catálogo (e por que são esses)
 *
 * | campo           | valor                 | uso na base |
 * |-----------------|-----------------------|-------------|
 * | dateType        | Relative              | 94%         |
 * | dateReference   | Source (hora da ação) | 88%         |
 * | lastEvent       | true (só o último)    | 82%         |
 * | act (1ª regra)  | IncludeOr             | forçado pela tela |
 *
 * `Fixed` é 10 a 25x mais caro de gerar (p95 de 7,8s contra 139ms da janela relativa de 1 a 30
 * dias) — evitar.
 *
 * ## As duas referências de data NÃO são "uma melhor que a outra"
 *
 * - `Source` (hora da ação) lê `eventSource.externalDate` — a data do FATO. Em agendamento é
 *   `appointment.start`; em `zandupay_cancelamento_agendado` é a data FUTURA em que o
 *   cancelamento entra em vigor.
 * - `Event` (hora do evento) lê `event.createdAt` — QUANDO FOI REGISTRADO. É o que responde
 *   "marcou há quanto tempo".
 *
 * Usar as duas na mesma regra permite perguntas que a base inteira nunca fez (ver
 * `amanha-marcado-ha-tempo`).
 *
 * ## Limites do motor que travam ideias
 *
 * 1. **Combinação é por pessoa, nunca por item.** `AND` e `NOT` cruzam `personId`
 *    (generate-audience.service.ts). "Faltou no agendamento X" e "tem horário amanhã" casam se a
 *    pessoa tiver os dois em QUALQUER combinação, não no mesmo agendamento.
 * 2. **Não há filtro por valor.** `eventSource.value` guarda o total da compra, mas
 *    `AudienceRuleCreateDto` não tem campo de valor. "Compras acima de R$ 500" é inexpressível.
 * 3. **Não há contagem.** "Faltou duas vezes" é inexpressível.
 *
 * ## Unidades
 *
 * `dateFrom`/`dateTo` são SEGUNDOS. Em regra relativa são deslocamentos a partir de agora:
 * negativo = passado, positivo = futuro. Use os helpers `hours()` e `days()`.
 */

const HOUR_IN_SECONDS = 3600
const DAY_IN_SECONDS = 86400

export function hours(n: number): number {
    return n * HOUR_IN_SECONDS
}

export function days(n: number): number {
    return n * DAY_IN_SECONDS
}

// =====================================================================================
// Automação sugerida
// =====================================================================================

/**
 * Ações disponíveis, exatamente como `automacao-manager-acts.svelte#loadActs` as oferece.
 *
 * O enum `AutomationAct` do automation-api tem mais membros (`automacao_pipeline_mover`,
 * `automacao_pipeline_status`, `automacao_comunicacao_enviar`, `automacao_classificacao_classificar`),
 * mas eles estão comentados na tela — não são alcançáveis hoje. Só entram aqui os que são.
 *
 * Fica local de propósito: `src/modules` é espelhado em `30 - zandu-api/src/modules/api`, então
 * criar um enum lá obriga a recopiar. Se essa duplicação incomodar, o lugar certo é
 * `src/modules/automationApi/automations/automation-act.enum.ts`, com a cópia correspondente.
 */
export enum AutomationAct {
    comunicacao_enviar_mensagem = 'automacao_comunicacao_enviar_mensagem',
    /** Só aparece com `window.beta`. */
    comunicacao_facebook = 'automacao_comunicacao_facebook',
    pipeline_novo = 'automacao_pipeline_novo',
    nps_solicitar = 'automacao_nps_solicitar',
    indica_contactar = 'automacao_indica_contactar',
    agenda_confirmar = 'automacao_agenda_confirmar',
    pdv_comprou = 'automacao_pdv_comprou',
}

/**
 * `repeat` é um COOLDOWN em segundos, não uma frequência.
 *
 * Em `automations.service#getPendingPersonsGeneral` o cálculo é
 * `dateTimeLimit = agora - repeat`, e quem foi contatado depois disso é pulado. Portanto:
 *
 * - `REPEAT_ALWAYS` (0) → o limite é "agora", ninguém fica de fora: dispara toda vez que a
 *   pessoa entrar no público.
 * - `REPEAT_NEVER` → o limite fica 60 anos atrás, todo mundo já contatado fica de fora:
 *   uma vez por pessoa, para sempre.
 *
 * Em `automacao_agenda_2` o worker só olha `repeat === 0` como booleano
 * (`getPendingPersonsForScheduleConfirmation`), que ali significa "repetir a cada agendamento".
 */
export const REPEAT_ALWAYS = 0

/** 60 anos em segundos — é literalmente o que a tela grava ao escolher "Nunca". */
export const REPEAT_NEVER = 1892160000

export function repeatDays(n: number): number {
    return n * DAY_IN_SECONDS
}

export interface SuggestedAutomation {
    /**
     * Tipo da automação. É a ÚNICA decisão irreversível do fluxo: a tela desabilita o campo
     * depois de criada, porque as ações já foram montadas para ele.
     */
    source: AutomationSourceEnum
    /** Precisa estar entre as que `loadActs` oferece para este `source`. */
    act: AutomationAct
    /** Cooldown em segundos. Ver `REPEAT_ALWAYS` / `REPEAT_NEVER`. */
    repeat: number
    /** Minutos de espera depois que a pessoa entra no público (`Automation.delay`). */
    delay?: number
    /** Orientação do que escrever. Rascunho para o assistente partir, não texto final. */
    messageHint?: string
    /**
     * Texto pronto para envio, gravado em `automation.message`. Só para mensagem que serve a
     * qualquer empresa sem edição. Ausente, a tela de mensagem cai no padrão da ação.
     */
    message?: string
    /** "Descartar mensagem se não entregue em até", em horas. Ausente = 0 (nunca descartar). */
    expirationInHours?: number
    /** Alternativa melhor quando a empresa tem estrutura para ela. */
    note?: string
}

export enum TemplateCategory {
    lembrete = 'lembrete',
    relacionamento = 'relacionamento',
    reativacao = 'reativacao',
    vendas = 'vendas',
    retencao = 'retencao',
    base = 'base',
}

export enum TemplateOrigin {
    /** Destilado de configurações que já existem em produção (com o viés de CS descrito acima). */
    observed = 'observed',
    /** Proposta nova. Sem uso medido — nasceu de evento vivo que nenhum público consome. */
    proposed = 'proposed',
}

/** Saúde do evento que o template consome, medida por `audiencerule.lastPersonFoundAt`. */
export enum TemplateHealth {
    /** O evento chegou nas últimas 24h em toda a base. */
    ok = 'ok',
    /** O evento existe mas parou de chegar há meses — confirmar antes de oferecer. */
    degraded = 'degraded',
}

/** Custo de geração, medido em `audience.audienceTimeMs`. */
export enum TemplateCost {
    low = 'low',
    medium = 'medium',
    high = 'high',
}

export interface TemplateParamOption {
    value: number | string
    label: string
}

export interface TemplateParam {
    key: string
    label: string
    /** `services` espera `TemplateService[]`; `date` espera `Date` (ou null, campo limpo); os demais esperam número. */
    type: 'hours' | 'days' | 'years' | 'services' | 'toggle' | 'date'
    default: any
    options?: TemplateParamOption[]
    help?: string
}

/** Serviço da própria empresa, vindo do cadastro — não texto digitado pelo usuário. */
export interface TemplateService {
    serviceId: string
    name: string
}

export interface TemplateUsage {
    /** Empresas que têm um público com esta configuração. Ver ressalva no topo do arquivo. */
    empresas: number
    /** Públicos com esta configuração. */
    publicos: number
    /** % das regras que já encontraram alguém pelo menos uma vez. */
    everFired: number
}

export interface AudienceTemplateBuild {
    audience: AudienceCreateDto
    /** Na ordem de aplicação. A 1ª é sempre `IncludeOr` — a tela força isso. */
    rules: AudienceRuleCreateDto[]
}

export interface AudienceTemplate {
    id: string
    title: string
    /** O que ESTE público é — quem entra nele. */
    description: string
    /**
     * O que DÁ PARA FAZER com ele. A lista existe para abrir a cabeça de quem nunca montou
     * campanha: o usuário costuma entender o filtro e não enxergar a oportunidade.
     * As ações citadas existem em `AutomationAct` (automation-api).
     */
    useCases: string[]
    category: TemplateCategory
    origin: TemplateOrigin
    /**
     * O que fazer com o público, para o assistente não parar no meio da jornada.
     *
     * `AutomationGroup.audienceId` é obrigatório no schema: automação não existe sem público.
     * O contrário também quase não existe — um público sem automação não entrega nada sozinho.
     * Por isso o template carrega os dois lados.
     */
    suggestedAutomation: SuggestedAutomation
    /** Eventos consumidos — o assistente usa para checar se a empresa tem esse dado. */
    events: string[]
    params: TemplateParam[]
    /** Ausente nos `proposed`. */
    usage?: TemplateUsage
    health: TemplateHealth
    cost: TemplateCost
    /** Imprecisão conhecida, quando existe. Mostrar ao usuário antes de salvar. */
    caveat?: string
    /**
     * Combinação de parâmetros que monta uma regra quebrada. Devolve o texto para o usuário, ou
     * null se está tudo certo. A tela não deixa conferir nem criar enquanto houver erro.
     */
    validate?: (params?: Record<string, any>) => string | null
    build: (params?: Record<string, any>) => AudienceTemplateBuild
}

// =====================================================================================
// Filtro de conteúdo
// =====================================================================================

/**
 * ATENÇÃO — não ligar sem verificar antes.
 *
 * O filtro por NOME do serviço é frágil: na base convivem `botox` (433 regras),
 * `toxina botulínica` (135), `toxina botulinica` sem acento (24), `toxina` (12), `[botox]` (20)
 * e `toxina botulínica,botox` (25) — todo mundo reescrevendo a mesma lista de sinônimos.
 * Medido em pares idênticos (mesmo evento, mesma janela, mesma referência), a regra com filtro
 * de texto tem 2 a 6 pontos percentuais menos de eficácia, e é 2,4x mais provável de nunca
 * disparar (8,8% contra 3,7% na regra mais usada da base).
 *
 * O filtro por ID resolveria: `create-event.processor.ts#getContentIds` varre o `data` do evento
 * e guarda TODO UUID em `eventSource.contentId`; `generate-audience.service#evaluateRule`
 * concatena `contentId.join(',')` no texto avaliado; e `event-rule-evaluation.service` quebra
 * esse texto por vírgula, então `[<uuid>]` casa por igualdade exata.
 *
 * Falta confirmar em produção se os eventos `agendamento_*` carregam o serviceId no `data`.
 * Até lá o catálogo emite filtro por nome.
 */
const USE_SERVICE_ID_FILTER = false

export function buildServiceFilter(services: TemplateService[] = []): string {
    if (!services?.length) return ''
    if (USE_SERVICE_ID_FILTER) {
        // colchetes = igualdade exata; vírgula = OU
        return services.map((el) => '[' + el.serviceId + ']').join(',')
    }
    // por nome: `contém`, separado por vírgula (OU) — é o que a base usa hoje
    return services
        .map((el) => el.name.trim())
        .filter((el) => !!el)
        .join(',')
}

function serviceLabel(services: TemplateService[] = []): string {
    if (!services?.length) return ''
    if (services.length === 1) return services[0].name
    return services.length + ' procedimentos'
}

// =====================================================================================
// Fábricas de regra
// =====================================================================================

/** Aplica os defaults do catálogo. Passe só o que difere. */
function rule(partial: Partial<AudienceRuleCreateDto>): AudienceRuleCreateDto {
    return {
        act: AudienceRuleAct.IncludeOr,
        event: '',
        value: '',
        dateType: AudienceRuleDateType.Relative,
        dateReference: AudienceRuleDateReference.Source,
        lastEvent: true,
        dateFrom: 0,
        dateTo: 0,
        ...partial,
    }
}

/**
 * "Tire quem já tem agendamento marcado."
 *
 * É a segunda regra mais usada da base (570 + 269 regras, 257 + 142 empresas) e aparece sempre
 * como complemento, nunca sozinha — por isso é função, não template.
 */
export function excludeAlreadyScheduled(value: string = '', aheadDays: number = 30): AudienceRuleCreateDto {
    return rule({
        act: AudienceRuleAct.Exclude,
        event: 'agendamento_confirmado,agendamento_criado',
        value,
        dateFrom: 0,
        dateTo: days(aheadDays),
    })
}

/** Todos os desfechos de um agendamento — usado para "só quem ainda não reagiu". */
const ALL_APPOINTMENT_EVENTS = [
    'agendamento_cancelado_empresa',
    'agendamento_cancelado_usuario',
    'agendamento_compareceu',
    'agendamento_confirmado',
    'agendamento_faltou',
    'agendamento_remarcado',
    'agendamento_remarcado_descontar',
].join(',')

/** O que 74 empresas digitam hoje para dizer "serviço de entrada". */
const DEFAULT_ENTRY_SERVICES = 'avaliação,consulta'

function param(p: TemplateParam): TemplateParam {
    return p
}

/** Opções de "Dias de espera". Lista fechada: número digitado livre passava do período e quebrava o público. */
const GRACE_OPTIONS: TemplateParamOption[] = [
    { value: 1, label: '1 dia' },
    { value: 2, label: '2 dias' },
    { value: 3, label: '3 dias' },
    { value: 5, label: '5 dias' },
    { value: 7, label: '7 dias' },
]

/**
 * A janela "de `sinceKey` dias atrás até `graceKey` dias atrás" só existe se a segunda for mais
 * recente. Com espera igual ou maior que o período, `dateFrom` passa de `dateTo` e o público
 * quebra em silêncio — a regra é gravada, mas não significa nada.
 */
function windowIsOpen(sinceKey: string, sinceDefault: number, graceKey: string, graceDefault: number, message: (since: number, grace: number) => string) {
    return (p: Record<string, any> = {}): string | null => {
        const since = +(p[sinceKey] ?? sinceDefault)
        const grace = +(p[graceKey] ?? graceDefault)
        return grace >= since ? message(since, grace) : null
    }
}

/**
 * "Avaliação sem conversão" inclui quem compareceu à entrada e exclui quem compareceu ao destino,
 * com o MESMO evento e janelas sobrepostas. Destino vazio vira "compareceu a qualquer coisa" e a
 * exclusão remove a própria avaliação: o público fica sempre zerado. Destino que casa com a entrada
 * faz o mesmo para esses serviços. O filtro é por nome (`contém`), então a comparação também é.
 */
function targetIsAfterEntry(p: Record<string, any> = {}): string | null {
    const targets: TemplateService[] = p.targetServices || []
    if (!targets.length) {
        return 'Escolha os procedimentos de destino. Sem eles, a própria avaliação conta como "já fez o procedimento" e ninguém vai receber.'
    }
    const entryTerms = (buildServiceFilter(p.entryServices) || DEFAULT_ENTRY_SERVICES)
        .split(',')
        .map((el) => el.trim().toLowerCase())
        .filter((el) => !!el)
    const clash = targets.find((t) => {
        const name = (t.name || '').trim().toLowerCase()
        return !!name && entryTerms.some((term) => name.includes(term) || term.includes(name))
    })
    if (clash) {
        return '"' + clash.name + '" também conta como serviço de entrada. Quem fez a avaliação com ele ficaria de fora na mesma hora. Escolha só o procedimento que deveria vir depois.'
    }
    return null
}

function graceMessage(since: number, grace: number): string {
    return 'A espera (' + grace + ' dias) precisa ser menor que o período (' + since + ' dias). Aumente o período ou diminua a espera.'
}

const PARAM_SERVICES = param({
    key: 'services',
    label: 'Procedimentos',
    type: 'services',
    default: [],
    help: 'Deixe vazio para considerar qualquer procedimento. Escolha na lista de serviços da empresa — não digite.',
})

// =====================================================================================
// Templates observados — destilados do playbook do CS
// =====================================================================================

/** 1. Agendados para amanhã — implantado em 377 empresas. */
const agendadosAmanha: AudienceTemplate = {
    id: 'agendados-amanha',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_agenda_2,
        act: AutomationAct.agenda_confirmar,
        // a cada agendamento: 492 grupos usam isso, contra 381 em "uma vez". Lembrete precisa
        // sair toda vez que a pessoa marca, não uma vez na vida dela.
        repeat: REPEAT_ALWAYS,
        messageHint: 'Data, horário, endereço e um pedido de confirmação com resposta simples.',
    },
    title: 'Agendados para amanhã',
    description: 'Quem tem horário marcado para o dia seguinte.',
    useCases: [
        'Lembrete com data, horário e endereço.',
        'Pedido de confirmação automático, com a ação "confirmar agenda".',
        'Instruções de preparo do procedimento (não usar maquiagem, vir sem esmalte, jejum).',
        'Lembrar a política de cancelamento, para reduzir falta de última hora.',
    ],
    category: TemplateCategory.lembrete,
    origin: TemplateOrigin.observed,
    events: ['agendamento_criado'],
    usage: { empresas: 377, publicos: 379, everFired: 97.4 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        PARAM_SERVICES,
        param({
            key: 'excludeReacted',
            label: 'Ignorar quem já confirmou ou cancelou',
            type: 'toggle',
            default: false,
            help: 'Para só quem não confirmou, prefira o template "Tem horário amanhã e não confirmou".',
        }),
    ],
    build: (p = {}) => {
        const value = buildServiceFilter(p.services)
        const rules: AudienceRuleCreateDto[] = [rule({ event: 'agendamento_criado', value, dateFrom: hours(20), dateTo: hours(30) })]
        if (p.excludeReacted) {
            rules.push(
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: ALL_APPOINTMENT_EVENTS,
                    lastEvent: false,
                    dateFrom: hours(20),
                    dateTo: hours(30),
                }),
            )
        }
        const suffix = p.services?.length ? ' — ' + serviceLabel(p.services) : ''
        return { audience: { type: 'dynamic', title: 'Agendados para amanhã' + suffix }, rules }
    },
}

/** 2. Aniversariantes de hoje — implantado em 261 empresas. */
const aniversariantesHoje: AudienceTemplate = {
    id: 'aniversariantes-hoje',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // 330 dias: aniversário é anual, e o cooldown evita disparo duplicado na virada do dia
        repeat: repeatDays(330),
        messageHint: 'Parabéns curto e pessoal, com uma cortesia de validade definida.',
        // parabéns entregue no dia seguinte soa como esquecimento: melhor não chegar
        expirationInHours: 12,
    },
    title: 'Aniversariantes de hoje',
    description: 'Quem faz aniversário hoje.',
    useCases: [
        'Mensagem de parabéns com cortesia ou desconto de validade curta.',
        'Oferecer justamente um procedimento que a pessoa nunca fez.',
        'Etiquetar como "aniversariante do mês" para o comercial abordar com calma.',
        'Se ninguém entra neste público, provavelmente falta data de nascimento no cadastro.',
    ],
    category: TemplateCategory.relacionamento,
    origin: TemplateOrigin.observed,
    events: ['pessoa_aniversario'],
    usage: { empresas: 261, publicos: 264, everFired: 95.1 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [],
    build: () => ({
        audience: { type: 'dynamic', title: 'Aniversariantes de hoje' },
        rules: [rule({ event: 'pessoa_aniversario', dateFrom: -hours(12), dateTo: 0 })],
    }),
}

/** 3. Compareceram na última semana — implantado em 235 empresas. */
const posAtendimento: AudienceTemplate = {
    id: 'pos-atendimento',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_nps,
        act: AutomationAct.nps_solicitar,
        // 90 dias é o padrão absoluto: 319 grupos em 318 empresas
        repeat: repeatDays(90),
        messageHint: 'Uma pergunta só. Quanto mais curto, mais gente responde.',
    },
    title: 'Compareceram recentemente',
    description: 'Quem foi atendido nos últimos dias.',
    useCases: [
        'Pesquisa de satisfação (NPS) enquanto a experiência está fresca.',
        'Orientação de pós-procedimento: cuidados, o que evitar, o que é normal sentir.',
        'Convite para já deixar a próxima sessão marcada.',
        'Pedido de indicação; é o momento de maior boa vontade.',
        'Pedido de avaliação no Google.',
    ],
    category: TemplateCategory.relacionamento,
    origin: TemplateOrigin.observed,
    events: ['agendamento_compareceu'],
    usage: { empresas: 235, publicos: 235, everFired: 98.4 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'fromDays',
            label: 'A partir de',
            type: 'days',
            default: 7,
            options: [
                { value: 2, label: '2 dias atrás' },
                { value: 3, label: '3 dias atrás' },
                { value: 7, label: '7 dias atrás' },
                { value: 15, label: '15 dias atrás' },
            ],
        }),
        param({
            key: 'toDays',
            label: 'Até',
            type: 'days',
            default: 1,
            help: 'Dias atrás. O padrão de 1 evita pegar quem foi atendido hoje.',
        }),
        PARAM_SERVICES,
    ],
    validate: windowIsOpen('fromDays', 7, 'toDays', 1, (from, to) => '"Até" (' + to + ' dias atrás) precisa ser mais recente que "A partir de" (' + from + ' dias atrás).'),
    build: (p = {}) => {
        const fromDays = p.fromDays ?? 7
        const toDays = p.toDays ?? 1
        const value = buildServiceFilter(p.services)
        const suffix = p.services?.length ? ' — ' + serviceLabel(p.services) : ''
        return {
            audience: { type: 'dynamic', title: 'Compareceram nos últimos ' + fromDays + ' dias' + suffix },
            rules: [rule({ event: 'agendamento_compareceu', value, dateFrom: -days(fromDays), dateTo: -days(toDays) })],
        }
    },
}

/** 4. Chegando nas próximas horas — implantado em ~159 empresas. */
const chegandoEmBreve: AudienceTemplate = {
    id: 'chegando-em-breve',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_agenda_2,
        act: AutomationAct.agenda_confirmar,
        repeat: REPEAT_ALWAYS,
        messageHint: 'Localização, ponto de referência e o que trazer. Direto ao ponto.',
        // sem isto a ação cai no texto padrão dela, que pede confirmação — errado para quem
        // chega daqui a pouco. No WABA, o mesmo texto puxa o template de lembrete.
        message: DEFAULT_MESSAGES.lembrete.text,
    },
    title: 'Chegando nas próximas horas',
    description: 'Quem é atendido daqui a pouco.',
    useCases: [
        'Lembrete de última hora com localização, referência e estacionamento.',
        'Aviso de preparo imediato ("chegue 10 minutos antes", "venha sem maquiagem").',
        'Reduzir atraso, que desorganiza a agenda do dia inteiro.',
    ],
    category: TemplateCategory.lembrete,
    origin: TemplateOrigin.observed,
    events: ['agendamento_confirmado', 'agendamento_criado'],
    usage: { empresas: 159, publicos: 161, everFired: 100 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'aheadHours',
            label: 'Antecedência',
            type: 'hours',
            default: 2,
            options: [
                { value: 1, label: '1 hora' },
                { value: 2, label: '2 horas' },
                { value: 3, label: '3 horas' },
                { value: 4, label: '4 horas' },
            ],
        }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const aheadHours = p.aheadHours ?? 2
        const value = buildServiceFilter(p.services)
        return {
            audience: { type: 'dynamic', title: 'Chegando em ' + aheadHours + ' hora(s)' },
            rules: [
                rule({
                    event: 'agendamento_confirmado,agendamento_criado',
                    value,
                    dateFrom: 0,
                    dateTo: hours(aheadHours),
                }),
            ],
        }
    },
}

/**
 * 5. Reativação — implantado em ~200 empresas, em 8 variantes que são a MESMA estrutura com a
 * janela deslocada. Três regras, um parâmetro de verdade.
 */
const reativacao: AudienceTemplate = {
    id: 'reativacao',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // 180 dias: reativação não pode insistir. 139 grupos em 110 empresas usam esse intervalo
        repeat: repeatDays(180),
        messageHint: 'Retomar pelo procedimento que a pessoa fez, com condição e prazo.',
        note: 'Converte mais como cartão no pipeline para ligação do que como mensagem em massa.',
    },
    title: 'Fizeram e não voltaram',
    description: 'Quem foi atendido há um tempo, não voltou desde então e não está com horário marcado.',
    useCases: [
        'Campanha de retorno com condição especial e prazo.',
        'Card no pipeline para o time ligar, um a um; costuma converter mais que mensagem em massa.',
        'Público de retargeting no Facebook.',
        'Pesquisa curta de "por que você parou de vir?"; o que volta daí costuma valer mais que a campanha.',
        'Ajustar o intervalo ao ciclo do procedimento: quem faz botox some por meses, quem faz limpeza de pele some por semanas.',
    ],
    category: TemplateCategory.reativacao,
    origin: TemplateOrigin.observed,
    events: ['agendamento_compareceu', 'agendamento_confirmado', 'agendamento_criado'],
    usage: { empresas: 200, publicos: 213, everFired: 92 },
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    params: [
        param({
            key: 'sinceDays',
            label: 'Última visita há',
            type: 'days',
            default: 90,
            options: [
                { value: 30, label: '1 mês' },
                { value: 60, label: '2 meses' },
                { value: 90, label: '3 meses' },
                { value: 120, label: '4 meses' },
                { value: 150, label: '5 meses' },
                { value: 180, label: '6 meses' },
            ],
        }),
        param({
            key: 'windowDays',
            label: 'Largura da faixa',
            type: 'days',
            default: 30,
            help: 'Quanto tempo antes de "última visita há" ainda entra. 30 dias é o padrão da base.',
        }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 90
        const windowDays = p.windowDays ?? 30
        const value = buildServiceFilter(p.services)
        const suffix = p.services?.length ? ' ' + serviceLabel(p.services) : ''
        const meses = Math.round(sinceDays / 30)
        return {
            audience: { type: 'dynamic', title: 'Fizeram' + suffix + ' há ' + meses + ' mês(es) e não voltaram' },
            rules: [
                rule({
                    event: 'agendamento_compareceu',
                    value,
                    dateFrom: -days(sinceDays + windowDays),
                    dateTo: -days(sinceDays),
                }),
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: 'agendamento_compareceu',
                    value,
                    dateFrom: -days(sinceDays),
                    dateTo: 0,
                }),
                excludeAlreadyScheduled(value),
            ],
        }
    },
}

/** 6. Compraram no PDV — implantado em 102 empresas. */
const compraramNoPdv: AudienceTemplate = {
    id: 'compraram-no-pdv',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_pdv,
        act: AutomationAct.pdv_comprou,
        repeat: repeatDays(30),
        messageHint: 'Agradecer, orientar o uso do que levou e sugerir o complemento.',
    },
    title: 'Compraram recentemente',
    description: 'Quem fechou compra no PDV no período.',
    useCases: [
        'Oferta de produto complementar ao que levou (home care depois do procedimento).',
        'Convite para migrar de compra avulsa para pacote ou assinatura.',
        'Pedido de avaliação no Google.',
        'Excluir de campanhas de desconto; quem acabou de comprar não precisa de incentivo.',
    ],
    category: TemplateCategory.vendas,
    origin: TemplateOrigin.observed,
    events: ['pdv_compra_realizada'],
    usage: { empresas: 102, publicos: 282, everFired: 95.2 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'sinceDays',
            label: 'Nos últimos',
            type: 'days',
            default: 30,
            options: [
                { value: 7, label: '7 dias' },
                { value: 30, label: '30 dias' },
                { value: 90, label: '90 dias' },
            ],
        }),
        PARAM_SERVICES,
        // a oferta de complemento só faz sentido para quem ainda não tem o complemento
        param({
            key: 'excludeServices',
            label: 'Exceto quem já comprou',
            type: 'services',
            default: [],
            help: 'O complemento que você quer oferecer. Quem comprou qualquer um deles fica de fora. Deixe vazio para não excluir ninguém.',
        }),
        param({
            key: 'excludeDays',
            label: 'Comprou o complemento nos últimos',
            type: 'days',
            default: 365,
            options: [
                { value: 90, label: '3 meses' },
                { value: 180, label: '6 meses' },
                { value: 365, label: '1 ano' },
                { value: 730, label: '2 anos' },
            ],
            help: 'Só vale se "Exceto quem já comprou" estiver preenchido.',
        }),
    ],
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 30
        const value = buildServiceFilter(p.services)
        const rules: AudienceRuleCreateDto[] = [rule({ event: 'pdv_compra_realizada', value, dateFrom: -days(sinceDays), dateTo: 0 })]
        // sem filtro a exclusão tiraria todo mundo que comprou qualquer coisa, inclusive o próprio público
        const excludeValue = buildServiceFilter(p.excludeServices)
        if (excludeValue) {
            rules.push(
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: 'pdv_compra_realizada',
                    value: excludeValue,
                    // todas as compras: com só a última, quem comprou o complemento e depois outra coisa voltaria
                    lastEvent: false,
                    dateFrom: -days(p.excludeDays ?? 365),
                    dateTo: 0,
                }),
            )
        }
        const suffix = excludeValue ? '; sem ' + serviceLabel(p.excludeServices) : ''
        return {
            audience: { type: 'dynamic', title: 'Compraram nos últimos ' + sinceDays + ' dias' + suffix },
            rules,
        }
    },
}

/** 7. Faltaram ou cancelaram — implantado em 73 empresas. */
const faltaramOuCancelaram: AudienceTemplate = {
    id: 'faltaram-ou-cancelaram',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // 5 dias: falta se repete, e você quer falar em cada uma. 344 grupos usam esse intervalo
        repeat: repeatDays(5),
        messageHint: 'Sem cobrança nem culpa. Oferecer horários concretos para remarcar.',
    },
    title: 'Faltaram ou cancelaram',
    description: 'Quem não compareceu ou desmarcou e não remarcou.',
    useCases: [
        'Mensagem oferecendo remarcação fácil, sem cobrança nem culpa.',
        'Card no pipeline para ligação; falta costuma ter motivo que só aparece falando.',
        'Oferecer os horários vagos da semana, aproveitando a agenda ociosa.',
        'Agir rápido: quem falta e não é procurado costuma não voltar mais.',
    ],
    category: TemplateCategory.reativacao,
    origin: TemplateOrigin.observed,
    events: ['agendamento_cancelado_usuario', 'agendamento_faltou'],
    usage: { empresas: 73, publicos: 104, everFired: 97 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'sinceDays',
            label: 'Nos últimos',
            type: 'days',
            default: 7,
            options: [
                { value: 3, label: '3 dias' },
                { value: 7, label: '7 dias' },
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
            ],
        }),
        param({ key: 'excludeRescheduled', label: 'Tirar quem já remarcou', type: 'toggle', default: true }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 7
        const value = buildServiceFilter(p.services)
        const rules: AudienceRuleCreateDto[] = [
            rule({
                event: 'agendamento_cancelado_usuario,agendamento_faltou',
                value,
                dateFrom: -days(sinceDays),
                dateTo: 0,
            }),
        ]
        if (p.excludeRescheduled !== false) rules.push(excludeAlreadyScheduled(value))
        return {
            audience: { type: 'dynamic', title: 'Faltaram ou cancelaram nos últimos ' + sinceDays + ' dias' },
            rules,
        }
    },
}

/** 8. Compareceram ontem — implantado em 62 empresas. */
const compareceramOntem: AudienceTemplate = {
    id: 'compareceram-ontem',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        repeat: repeatDays(7),
        messageHint: 'Perguntar como a pessoa está se sentindo e lembrar o cuidado das primeiras 24 horas.',
    },
    title: 'Compareceram ontem',
    description: 'Quem foi atendido no dia anterior.',
    useCases: [
        'Acompanhamento no dia seguinte: "como você está se sentindo?".',
        'Instruções de cuidado nas primeiras 24 horas.',
        'Janela boa para pedir avaliação, quando o resultado já apareceu.',
    ],
    category: TemplateCategory.relacionamento,
    origin: TemplateOrigin.observed,
    events: ['agendamento_compareceu'],
    usage: { empresas: 62, publicos: 63, everFired: 92.1 },
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [PARAM_SERVICES],
    build: (p = {}) => {
        const value = buildServiceFilter(p.services)
        return {
            audience: { type: 'dynamic', title: 'Compareceram ontem' },
            rules: [rule({ event: 'agendamento_compareceu', value, dateFrom: -hours(30), dateTo: -hours(20) })],
        }
    },
}

/**
 * 9. Novas indicações — implantado em 72 empresas.
 *
 * SAÚDE DEGRADADA. 75% das regras já encontraram alguém, mas a mediana é de 329 dias desde o
 * último achado: `indica_incluido` parou de chegar há cerca de 11 meses.
 */
const novasIndicacoes: AudienceTemplate = {
    id: 'novas-indicacoes',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_indica,
        act: AutomationAct.indica_contactar,
        repeat: repeatDays(10),
        messageHint: 'Citar quem indicou: é o que faz a mensagem não parecer abordagem fria.',
    },
    title: 'Novas indicações',
    description: 'Quem foi indicado recentemente.',
    useCases: [
        'Contato rápido com o indicado, enquanto a recomendação está quente.',
        'Agradecer e recompensar quem indicou.',
        'Card no pipeline; indicação converte muito mais que lead frio.',
    ],
    category: TemplateCategory.relacionamento,
    origin: TemplateOrigin.observed,
    events: ['indica_incluido'],
    usage: { empresas: 72, publicos: 72, everFired: 84.9 },
    health: TemplateHealth.degraded,
    cost: TemplateCost.low,
    caveat: 'O evento `indica_incluido` não é registrado há cerca de 11 meses em toda a base. Confirmar se o fluxo de indicação ainda funciona antes de oferecer.',
    params: [
        param({
            key: 'sinceDays',
            label: 'Nos últimos',
            type: 'days',
            default: 3,
            options: [
                { value: 3, label: '3 dias' },
                { value: 7, label: '7 dias' },
            ],
        }),
    ],
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 3
        return {
            audience: { type: 'dynamic', title: 'Novas indicações' },
            rules: [rule({ event: 'indica_incluido', dateFrom: -days(sinceDays), dateTo: 0 })],
        }
    },
}

/**
 * 10. Base completa — implantado em 38 empresas.
 *
 * CUSTO ALTO: data fixa + todos os eventos dá mediana de 299ms e p95 de 17,7s por geração,
 * contra 20ms/139ms de uma janela relativa.
 */
const baseCompleta: AudienceTemplate = {
    id: 'base-completa',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // comunicado geral: uma vez por pessoa e ponto
        repeat: REPEAT_NEVER,
        messageHint: 'Um aviso, uma informação. Sem oferta junto.',
    },
    title: 'Todos os contatos do período',
    description: 'Todo mundo cadastrado ou atualizado entre duas datas.',
    useCases: [
        'Comunicado geral: mudança de endereço, novo horário, campanha sazonal.',
        'Base para público personalizado do Facebook.',
        'Antes de usar, considere um público estático: sai muito mais barato e dá o mesmo resultado.',
    ],
    category: TemplateCategory.base,
    origin: TemplateOrigin.observed,
    events: ['pessoa_criada', 'pessoa_editada'],
    usage: { empresas: 38, publicos: 59, everFired: 100 },
    health: TemplateHealth.ok,
    cost: TemplateCost.high,
    // `date`, não `days`: como `days` sem opções, a tela desenhava campo numérico, e "2024" virava
    // 2024 milissegundos depois de 01/01/1970
    params: [
        param({ key: 'from', label: 'De', type: 'date', default: null, help: 'Primeiro dia do período.' }),
        param({ key: 'to', label: 'Até', type: 'date', default: null, help: 'Último dia do período (entra inteiro).' }),
    ],
    validate: (p = {}) => {
        if (!(p.from instanceof Date) || !(p.to instanceof Date)) return 'Escolha a data inicial e a data final do período.'
        if (+p.from > +p.to) return 'A data inicial precisa ser anterior ou igual à data final.'
        return null
    },
    build: (p = {}) => {
        const from: Date = p.from ?? new Date()
        const to: Date = p.to ?? new Date()
        return {
            audience: { type: 'dynamic', title: 'Todos os contatos do período' },
            rules: [
                rule({
                    event: 'pessoa_criada,pessoa_editada',
                    dateType: AudienceRuleDateType.Fixed,
                    lastEvent: false,
                    dateFrom: Math.floor(+from / 1000),
                    // + 1 dia para incluir o dia inteiro, como a tela de regra faz
                    dateTo: Math.floor((+to + 86400999) / 1000),
                }),
            ],
        }
    },
}

// =====================================================================================
// Templates propostos — sem uso medido
// =====================================================================================

/**
 * Tem horário amanhã e já faltou antes.
 *
 * Usa `IncludeAnd`, que aparece em só 20 empresas da base — não porque não sirva, mas porque o
 * playbook do CS nunca precisou dele. O cruzamento por pessoa é semanticamente CORRETO aqui:
 * "tem histórico de falta" é atributo da pessoa, não de um agendamento.
 */
const amanhaJaFaltou: AudienceTemplate = {
    id: 'amanha-ja-faltou',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        repeat: REPEAT_ALWAYS,
        messageHint: 'Pedir confirmação explícita, com resposta ativa. Citar a política de cancelamento se houver.',
        note: 'A ação certa aqui é ligação humana, que é o motivo de o público existir. Cartão no pipeline (automacao_pipeline + automacao_pipeline_novo) serve melhor, mas exige board e lista configurados: por isso o padrão é mensagem.',
    },
    title: 'Tem horário amanhã e já faltou antes',
    description: 'Quem é atendido amanhã e tem histórico de falta (maior chance de faltar novamente).',
    useCases: [
        'Pedir confirmação explícita, com resposta ativa.',
        'Avisar sobre taxa de reserva ou política de cancelamento, se houver.',
        'Se a pessoa não confirmar, oferecer o horário para a lista de espera antes de perdê-lo.',
    ],
    category: TemplateCategory.lembrete,
    origin: TemplateOrigin.proposed,
    events: ['agendamento_criado', 'agendamento_confirmado', 'agendamento_faltou'],
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    params: [
        param({
            key: 'lookbackDays',
            label: 'Histórico de falta dos últimos',
            type: 'days',
            default: 180,
            options: [
                { value: 90, label: '3 meses' },
                { value: 180, label: '6 meses' },
                { value: 365, label: '1 ano' },
            ],
        }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const lookbackDays = p.lookbackDays ?? 180
        const value = buildServiceFilter(p.services)
        return {
            audience: { type: 'dynamic', title: 'Horário amanhã; já faltou antes' },
            rules: [
                rule({ event: 'agendamento_criado,agendamento_confirmado', value, dateFrom: hours(20), dateTo: hours(30) }),
                rule({
                    act: AudienceRuleAct.IncludeAnd,
                    event: 'agendamento_faltou',
                    // todos os eventos: pega quem faltou mesmo que depois tenha remarcado
                    lastEvent: false,
                    dateFrom: -days(lookbackDays),
                    dateTo: 0,
                }),
            ],
        }
    },
}

/**
 * Tem horário amanhã e ainda não confirmou.
 *
 * Uma regra só. `lastEvent: true` com `agendamento_criado` significa que o status ATUAL do
 * agendamento ainda é "criado" — não avançou para confirmado nem para cancelado. Mais preciso e
 * mais barato que incluir tudo e excluir quem reagiu.
 */
const amanhaNaoConfirmou: AudienceTemplate = {
    id: 'amanha-nao-confirmou',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_agenda_2,
        act: AutomationAct.agenda_confirmar,
        repeat: REPEAT_ALWAYS,
        messageHint: 'Segunda tentativa: mudar o tom e, se der, o canal.',
    },
    title: 'Tem horário amanhã e não confirmou',
    description: 'Quem é atendido amanhã e cujo agendamento continua sem confirmação.',
    useCases: [
        'Segunda tentativa de confirmação, de preferência por outro canal.',
        'Ligação no fim da tarde anterior, quando a mensagem não teve resposta.',
        'Liberar o horário para a lista de espera se não houver confirmação até certa hora.',
        'Medir a taxa de confirmação da clínica: se este público vive cheio, o problema está na mensagem de lembrete, não no cliente.',
    ],
    category: TemplateCategory.lembrete,
    origin: TemplateOrigin.proposed,
    events: ['agendamento_criado'],
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [PARAM_SERVICES],
    build: (p = {}) => {
        const value = buildServiceFilter(p.services)
        return {
            audience: { type: 'dynamic', title: 'Horário amanhã; não confirmou' },
            rules: [rule({ event: 'agendamento_criado', value, dateFrom: hours(20), dateTo: hours(30) })],
        }
    },
}

/**
 * Marcado há muito tempo, atendimento amanhã.
 *
 * Usa as DUAS referências de data na mesma configuração: `Event` responde "marcou quando" e
 * `Source` responde "é atendido quando". Nenhuma regra da base faz isso.
 */
const amanhaMarcadoHaTempo: AudienceTemplate = {
    id: 'amanha-marcado-ha-tempo',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_agenda_2,
        act: AutomationAct.agenda_confirmar,
        repeat: REPEAT_ALWAYS,
        messageHint: 'Reapresentar o combinado: procedimento, profissional, valor e preparo. Quem marcou há meses não lembra.',
    },
    title: 'Marcado há muito tempo, atendimento amanhã',
    description: 'Quem marcou faz semanas ou meses e é atendido amanhã (maiores chances de esquecimento).',
    useCases: [
        'Reconfirmação reforçada: quem marcou há meses muitas vezes não lembra do compromisso.',
        'Reenviar o que foi combinado — procedimento, valor, profissional, preparo.',
        'Testar se vale limitar a antecedência máxima de agendamento na clínica.',
    ],
    category: TemplateCategory.lembrete,
    origin: TemplateOrigin.proposed,
    events: ['agendamento_criado'],
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    caveat: 'O motor cruza por PESSOA, não por agendamento. Quem marcou algo há meses e tem outro horário amanhã entra, mesmo sendo agendamentos diferentes.',
    params: [
        param({
            key: 'leadDays',
            label: 'Marcado há mais de',
            type: 'days',
            default: 30,
            options: [
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
                { value: 60, label: '60 dias' },
            ],
        }),
        param({ key: 'lookbackDays', label: 'Procurar marcações até', type: 'days', default: 365 }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const leadDays = p.leadDays ?? 30
        const lookbackDays = p.lookbackDays ?? 365
        const value = buildServiceFilter(p.services)
        return {
            audience: { type: 'dynamic', title: 'Marcado há mais de ' + leadDays + ' dias; atendimento amanhã' },
            rules: [
                // quando foi REGISTRADO
                rule({
                    event: 'agendamento_criado',
                    value,
                    dateReference: AudienceRuleDateReference.Event,
                    dateFrom: -days(lookbackDays),
                    dateTo: -days(leadDays),
                }),
                // quando é ATENDIDO
                rule({
                    act: AudienceRuleAct.IncludeAnd,
                    event: 'agendamento_criado',
                    value,
                    dateReference: AudienceRuleDateReference.Source,
                    dateFrom: hours(20),
                    dateTo: hours(30),
                }),
            ],
        }
    },
}

/**
 * Orçamento sem compra.
 *
 * O buraco mais óbvio do catálogo: `pdv_orcamento_criado` está vivo (mediana de 6 dias desde o
 * último achado) e só 14 das 576 empresas têm algum público que o use. É lead quente parado.
 * O evento carrega os nomes dos serviços no `content` (pos-api/quotations.service.ts), então o
 * filtro por procedimento funciona.
 */
const orcamentoSemCompra: AudienceTemplate = {
    id: 'orcamento-sem-compra',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        repeat: repeatDays(30),
        messageHint: 'Retomar o orçamento pelo nome do procedimento, com validade e uma pergunta aberta sobre o que faltou.',
        note: 'Cartão no pipeline funciona melhor: orçamento parado costuma ter objeção que só aparece conversando.',
    },
    title: 'Fizeram orçamento e não compraram',
    description: 'Quem recebeu um orçamento no período e não fechou compra depois.',
    useCases: [
        'Retomar o orçamento com prazo de validade; o motivo mais comum de não fechar é esquecimento.',
        'Card no pipeline para o consultor ligar com a proposta na mão.',
        'Oferecer parcelamento ou condição diferente, se o obstáculo for preço.',
        'Descobrir a objeção real com uma pergunta aberta, e levar isso para a equipe de vendas.',
        'Público de retargeting no Facebook.',
        'Acompanhar o tamanho deste público como indicador: se cresce, a conversão do orçamento está caindo.',
    ],
    category: TemplateCategory.vendas,
    origin: TemplateOrigin.proposed,
    events: ['pdv_orcamento_criado', 'pdv_compra_realizada'],
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'sinceDays',
            label: 'Orçamentos dos últimos',
            type: 'days',
            default: 30,
            options: [
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
                { value: 60, label: '60 dias' },
                { value: 90, label: '90 dias' },
            ],
        }),
        param({
            key: 'graceDays',
            label: 'Dias de espera',
            type: 'days',
            default: 2,
            options: GRACE_OPTIONS,
            help: 'Quantos dias depois do orçamento a pessoa passa a entrar no público. Com 2, quem recebeu o orçamento hoje ou ontem ainda não é abordado.',
        }),
        PARAM_SERVICES,
    ],
    validate: windowIsOpen('sinceDays', 30, 'graceDays', 2, graceMessage),
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 30
        const graceDays = p.graceDays ?? 2
        const value = buildServiceFilter(p.services)
        const suffix = p.services?.length ? ' — ' + serviceLabel(p.services) : ''
        return {
            audience: { type: 'dynamic', title: 'Orçamento sem compra' + suffix },
            rules: [
                rule({
                    event: 'pdv_orcamento_criado',
                    value,
                    dateFrom: -days(sinceDays),
                    dateTo: -days(graceDays),
                }),
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: 'pdv_compra_realizada',
                    dateFrom: -days(sinceDays),
                    dateTo: 0,
                }),
            ],
        }
    },
}

/**
 * Avaliação que não virou procedimento.
 *
 * `avaliação` aparece em 74 empresas e `consulta` em 31 — elas já filtram por isso, mas só para
 * INCLUIR. Ninguém mede a conversão da avaliação, que é o funil comercial da clínica.
 */
const avaliacaoSemConversao: AudienceTemplate = {
    id: 'avaliacao-sem-conversao',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        repeat: repeatDays(30),
        messageHint: 'Retomar o que foi conversado na avaliação. Dúvida e medo seguram mais venda que preço.',
        note: 'Cartão no pipeline para o consultor que atendeu costuma converter mais.',
    },
    title: 'Fizeram avaliação e não fecharam',
    description: 'Quem veio para avaliação ou consulta, não voltou para fazer o procedimento e não tem horário marcado.',
    useCases: [
        'Ligação do consultor retomando o que foi conversado na avaliação.',
        'Conteúdo educativo sobre o procedimento avaliado: dúvida e medo seguram mais venda que preço.',
        'Condição especial com prazo, para quem já demonstrou intenção.',
        'Acompanhar a taxa de conversão da avaliação por profissional.',
    ],
    category: TemplateCategory.vendas,
    origin: TemplateOrigin.proposed,
    events: ['agendamento_compareceu', 'agendamento_confirmado', 'agendamento_criado'],
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    caveat: 'O filtro de serviço compara pelo nome. Um procedimento de destino com nome parecido com o da avaliação (ex.: "Avaliação facial") também deixa de fora quem fez essa avaliação.',
    params: [
        param({
            key: 'entryServices',
            label: 'Serviços de entrada',
            type: 'services',
            default: [],
            help: 'O que a pessoa veio fazer. Vazio usa "' + DEFAULT_ENTRY_SERVICES + '".',
        }),
        param({
            key: 'targetServices',
            label: 'Procedimentos de destino',
            type: 'services',
            default: [],
            help: 'O que ela deveria ter fechado depois.',
        }),
        param({
            key: 'sinceDays',
            label: 'Avaliações dos últimos',
            type: 'days',
            default: 45,
            options: [
                { value: 30, label: '30 dias' },
                { value: 45, label: '45 dias' },
                { value: 90, label: '90 dias' },
            ],
        }),
        param({
            key: 'graceDays',
            label: 'Dias de espera',
            type: 'days',
            default: 3,
            options: GRACE_OPTIONS,
            help: 'Quantos dias depois da avaliação a pessoa passa a entrar no público. Dá tempo de ela decidir sozinha antes de receber mensagem.',
        }),
    ],
    validate: (p = {}) => windowIsOpen('sinceDays', 45, 'graceDays', 3, graceMessage)(p) || targetIsAfterEntry(p),
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 45
        const graceDays = p.graceDays ?? 3
        const entry = buildServiceFilter(p.entryServices) || DEFAULT_ENTRY_SERVICES
        const target = buildServiceFilter(p.targetServices)
        return {
            audience: { type: 'dynamic', title: 'Avaliação sem conversão' },
            rules: [
                rule({
                    event: 'agendamento_compareceu',
                    value: entry,
                    dateFrom: -days(sinceDays),
                    dateTo: -days(graceDays),
                }),
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: 'agendamento_compareceu',
                    value: target,
                    dateFrom: -days(sinceDays),
                    dateTo: 0,
                }),
                excludeAlreadyScheduled(target),
            ],
        }
    },
}

/**
 * Primeira visita.
 *
 * Veio agora e nunca tinha vindo. A conversão da primeira para a segunda visita é o momento de
 * maior alavancagem em clínica de estética, e nenhum público da base olha para isso.
 */
const primeiraVisita: AudienceTemplate = {
    id: 'primeira-visita',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // primeira visita acontece uma vez: cooldown eterno evita repetir se a pessoa reaparecer
        repeat: REPEAT_NEVER,
        messageHint: 'Boas-vindas explicando a jornada: o que esperar, em quanto tempo, quantas sessões.',
    },
    title: 'Vieram pela primeira vez',
    description: 'Quem foi atendido recentemente e não tinha histórico nenhum antes disso.',
    useCases: [
        'Boas-vindas explicando a jornada: o que esperar, em quanto tempo, quantas sessões.',
        'Convite para a segunda visita com condição de primeira recompra.',
        'Pesquisa de primeira impressão, separada do NPS geral.',
        'Apresentar a equipe e os canais de contato, para criar vínculo além do profissional que atendeu.',
        'Acompanhar quantos voltam: quem não retorna depois da primeira visita quase nunca volta sozinho.',
    ],
    category: TemplateCategory.relacionamento,
    origin: TemplateOrigin.proposed,
    events: ['agendamento_compareceu'],
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    caveat: 'Empresa com pouco tempo de Zandu vê quase todo mundo como "primeira vez", porque não há histórico anterior à migração. Conferir a idade da conta antes de confiar no resultado.',
    params: [
        param({
            key: 'sinceDays',
            label: 'Atendidos nos últimos',
            type: 'days',
            default: 30,
            options: [
                { value: 7, label: '7 dias' },
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
            ],
        }),
        param({
            key: 'graceDays',
            label: 'Dias de espera',
            type: 'days',
            default: 2,
            options: GRACE_OPTIONS,
            help: 'Quantos dias depois do atendimento a pessoa passa a entrar no público. Evita mandar mensagem no mesmo dia em que ela saiu da clínica.',
        }),
        param({
            key: 'historyYears',
            label: 'Sem histórico nos últimos',
            type: 'years',
            default: 3,
            options: [
                { value: 1, label: '1 ano' },
                { value: 2, label: '2 anos' },
                { value: 3, label: '3 anos' },
            ],
        }),
    ],
    validate: windowIsOpen('sinceDays', 30, 'graceDays', 2, graceMessage),
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 30
        const graceDays = p.graceDays ?? 2
        const historyYears = p.historyYears ?? 3
        return {
            audience: { type: 'dynamic', title: 'Vieram pela primeira vez' },
            rules: [
                rule({ event: 'agendamento_compareceu', dateFrom: -days(sinceDays), dateTo: -days(graceDays) }),
                rule({
                    act: AudienceRuleAct.Exclude,
                    event: 'agendamento_compareceu',
                    lastEvent: false,
                    dateFrom: -days(historyYears * 365),
                    dateTo: -days(sinceDays),
                }),
            ],
        }
    },
}

/**
 * Comprou uma vez e não voltou.
 *
 * Mesma estrutura da reativação, mas sobre COMPRA em vez de comparecimento. 111 empresas usam
 * `pdv_compra_realizada`, nenhuma nesse formato.
 */
const comprouESumiu: AudienceTemplate = {
    id: 'comprou-e-sumiu',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        repeat: repeatDays(180),
        messageHint: 'Recompra do mesmo item, no ciclo em que ele costuma acabar.',
    },
    title: 'Compraram e não voltaram',
    description: 'Quem comprou há um tempo e não comprou mais desde então.',
    useCases: [
        'Campanha de recompra do mesmo item, no ciclo em que ele costuma acabar.',
        'Migrar de compra avulsa para pacote ou assinatura, que é o que trava a recorrência.',
        'Card no pipeline para contato do comercial.',
        'Separar de quem só agenda e nunca compra: são problemas comerciais diferentes.',
    ],
    category: TemplateCategory.reativacao,
    origin: TemplateOrigin.proposed,
    events: ['pdv_compra_realizada'],
    health: TemplateHealth.ok,
    cost: TemplateCost.medium,
    params: [
        param({
            key: 'sinceDays',
            label: 'Última compra há',
            type: 'days',
            default: 90,
            options: [
                { value: 30, label: '1 mês' },
                { value: 60, label: '2 meses' },
                { value: 90, label: '3 meses' },
                { value: 180, label: '6 meses' },
            ],
        }),
        param({ key: 'windowDays', label: 'Largura da faixa', type: 'days', default: 30 }),
        param({ key: 'excludeScheduled', label: 'Tirar quem tem horário marcado', type: 'toggle', default: true }),
        PARAM_SERVICES,
    ],
    build: (p = {}) => {
        const sinceDays = p.sinceDays ?? 90
        const windowDays = p.windowDays ?? 30
        const value = buildServiceFilter(p.services)
        const rules: AudienceRuleCreateDto[] = [
            rule({
                event: 'pdv_compra_realizada',
                value,
                dateFrom: -days(sinceDays + windowDays),
                dateTo: -days(sinceDays),
            }),
            rule({
                act: AudienceRuleAct.Exclude,
                event: 'pdv_compra_realizada',
                value,
                dateFrom: -days(sinceDays),
                dateTo: 0,
            }),
        ]
        if (p.excludeScheduled !== false) rules.push(excludeAlreadyScheduled())
        const meses = Math.round(sinceDays / 30)
        return {
            audience: { type: 'dynamic', title: 'Compraram há ' + meses + ' mês(es) e não voltaram' },
            rules,
        }
    },
}

/**
 * Cancelamento agendado.
 *
 * `zandupay_cancelamento_agendado` é emitido por payments-api e NENHUM público da base o
 * consome. Dois detalhes que mudam o desenho:
 *
 * 1. O `externalDate` do evento é a data FUTURA em que o cancelamento entra em vigor
 *    (purchases.service.ts#generateScheduledCancelEvent), não a data do pedido. Por isso a
 *    janela olha para FRENTE, com referência `Source`.
 * 2. `zandupay_cancelamento_agendado_excluido` está em `exclusionEvents` do events-api: quando a
 *    pessoa volta atrás, o eventSource inteiro vira REMOVED e some das consultas, que filtram por
 *    status ativo. O público se limpa sozinho, sem precisar de regra de exclusão.
 */
const cancelamentoAgendado: AudienceTemplate = {
    id: 'cancelamento-agendado',
    suggestedAutomation: {
        source: AutomationSourceEnum.automacao_comunicacao,
        act: AutomationAct.comunicacao_enviar_mensagem,
        // nunca repetir: insistir com quem está saindo acelera a saída
        repeat: REPEAT_NEVER,
        delay: 0,
        messageHint: 'Perguntar o motivo antes de oferecer qualquer coisa. Pausa e downgrade convertem mais que desconto.',
        note: 'Cartão no pipeline com responsável e prazo é o ideal: a janela fecha na data do cancelamento.',
    },
    title: 'Vão cancelar a assinatura',
    description: 'Quem pediu cancelamento e tem a data marcada para os próximos dias. Sai sozinho do público se voltar atrás.',
    useCases: [
        'Contato humano antes da data; é a última janela para reverter.',
        'Oferecer pausa, downgrade ou troca de plano em vez do cancelamento.',
        'Entender o motivo: é o retorno mais honesto que a clínica vai receber.',
        'Prioridade máxima no pipeline, com responsável e prazo.',
        'Acompanhar o tamanho deste público como alerta antecipado de churn, semanas antes do faturamento cair.',
    ],
    category: TemplateCategory.retencao,
    origin: TemplateOrigin.proposed,
    events: ['zandupay_cancelamento_agendado'],
    health: TemplateHealth.ok,
    cost: TemplateCost.low,
    params: [
        param({
            key: 'aheadDays',
            label: 'Cancelam nos próximos',
            type: 'days',
            default: 30,
            options: [
                { value: 7, label: '7 dias' },
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
                { value: 60, label: '60 dias' },
            ],
        }),
    ],
    build: (p = {}) => {
        const aheadDays = p.aheadDays ?? 30
        return {
            audience: { type: 'dynamic', title: 'Cancelamento agendado' },
            rules: [
                rule({
                    event: 'zandupay_cancelamento_agendado',
                    dateReference: AudienceRuleDateReference.Source,
                    dateFrom: 0,
                    dateTo: days(aheadDays),
                }),
            ],
        }
    },
}

// =====================================================================================
// Catálogo
// =====================================================================================

export const AUDIENCE_TEMPLATES: AudienceTemplate[] = [
    // observados
    agendadosAmanha,
    aniversariantesHoje,
    posAtendimento,
    chegandoEmBreve,
    reativacao,
    compraramNoPdv,
    faltaramOuCancelaram,
    compareceramOntem,
    novasIndicacoes,
    baseCompleta,
    // propostos
    amanhaJaFaltou,
    amanhaNaoConfirmou,
    amanhaMarcadoHaTempo,
    orcamentoSemCompra,
    avaliacaoSemConversao,
    primeiraVisita,
    comprouESumiu,
    cancelamentoAgendado,
]

export function getTemplate(id: string): AudienceTemplate | undefined {
    return AUDIENCE_TEMPLATES.find((el) => el.id === id)
}

/** Só os que podem ser oferecidos sem ressalva. */
export function getHealthyTemplates(): AudienceTemplate[] {
    return AUDIENCE_TEMPLATES.filter((el) => el.health === TemplateHealth.ok && el.cost !== TemplateCost.high)
}

export function getTemplatesByCategory(category: TemplateCategory): AudienceTemplate[] {
    return AUDIENCE_TEMPLATES.filter((el) => el.category === category)
}

export function getTemplatesByOrigin(origin: TemplateOrigin): AudienceTemplate[] {
    return AUDIENCE_TEMPLATES.filter((el) => el.origin === origin)
}

/**
 * Monta o payload de preview a partir de um template, sem persistir nada.
 *
 * `POST /audiences/preview` (window.api.events.audiences.getPreview) aceita regras avulsas e
 * devolve `evaluatedRules[].totalDistinct`. Serve como pre-flight: dá para saber que o template
 * traria 0 pessoas ANTES de criar o público.
 *
 * Vale mais do que parece. Erro de configuração quase não existe — 98% das regras da base já
 * encontraram alguém. O que existe é falta de dado: há empresas com o público de aniversariantes
 * parado há 274 dias porque ninguém preenche data de nascimento.
 *
 * O endpoint aceita o público INTEIRO e devolve o total já combinado: `execute()` monta
 * `personsAndContent` aplicando OR/AND/NOT e só então retorna, e o controller aceita um
 * `audienceId` que não existe no banco ("é possível que audience não exista", diz o próprio
 * código). Ou seja: dá para mostrar o tamanho real do público ANTES de criar qualquer coisa.
 *
 * A tela de regra atual mostra número por regra isolada só porque `audience-regra.svelte` envia
 * uma regra por vez — é limitação de quem chama, não do endpoint.
 */
export function buildPreviewRules(template: AudienceTemplate, params: Record<string, any> = {}) {
    return template.build(params).rules
}

/**
 * Rascunho completo: público, regras, grupo e ação. Para o usuário isso é UMA coisa só.
 *
 * Os dois `Omit` refletem a ordem obrigatória de criação — cada id só existe depois da chamada
 * anterior:
 *
 *   1. `POST /audiences`            (events-api)     → audienceId
 *   2. `POST /audiences/:id/rules`  (events-api)     × N
 *   3. `POST /automation-groups`    (automation-api) → automationGroupId
 *   4. `POST /automations`          (automation-api)
 *
 * ATENÇÃO: são quatro chamadas em DOIS backends, sem transação. Falhar no passo 3 deixa um
 * público órfão — provavelmente a origem dos 156 públicos dinâmicos com zero regras que existem
 * hoje em produção. Quem chamar isto precisa de rollback (`DELETE /audiences/:id` no catch).
 *
 * Depois das regras, chame `generateAudience(audienceId)` como a tela manual faz: o público nasce
 * em stage 'new', e o cron só pega 'ready'. Sem essa chamada ele ficava até 12h parado, esperando
 * o fallback de `findAllPending`. A geração passa o stage para 'ready' e daí em diante vale o
 * `#evaluateNextUpdate` (4 minutos para audiência com menos de 20 minutos de vida).
 */
export interface CampaignDraft {
    audience: AudienceCreateDto
    rules: AudienceRuleCreateDto[]
    group: Omit<CreateAutomationGroupsDto, 'audienceId'>
    automation: Omit<CreateAutomationDto, 'automationGroupId'>
    /** Repassar ao usuário quando existir: é onde o template engana. */
    caveat?: string
    /** Alternativa melhor de ação, quando a empresa tiver estrutura. */
    note?: string
    /**
     * Orientação do que escrever — para a tela mostrar como dica ou placeholder.
     *
     * NÃO vai em `automation.message` de propósito: é instrução para quem redige, não texto para
     * o cliente receber. A mensagem final é sempre escrita (ou revisada) por uma pessoa.
     */
    messageHint?: string
}

export function buildCampaign(template: AudienceTemplate, params: Record<string, any> = {}, active: boolean = true): CampaignDraft {
    const built = template.build(params)
    const sa = template.suggestedAutomation
    return {
        audience: built.audience,
        rules: built.rules,
        group: {
            // a tela já faz isso ao escolher o público: o nome da automação herda o do público
            label: built.audience.title,
            source: sa.source,
            repeat: sa.repeat,
            status: active ? 100 : 70,
        },
        automation: {
            act: sa.act,
            delay: sa.delay ?? 0,
            expirationInHours: sa.expirationInHours ?? 0,
            // só `sa.message`, nunca `messageHint`: a dica é orientação de redação, não texto de
            // envio. Gravá-la aqui faria o cliente receber "Retomar o orçamento pelo nome do...".
            message: sa.message ?? '',
        },
        caveat: template.caveat,
        note: sa.note,
        messageHint: sa.messageHint,
    }
}
